/**
 * The carousel strip's page textures for this device's asset tier: their
 * layout (from the bake) and the URLs Portfolio.tsx streams them from.
 *
 * The URL shape — file name per tier, page index, `?v=` content hash — is
 * also written into index.html by scripts/bake-carousel-strip.mjs, whose
 * head script preloads the pages on "/" before any module runs. Both must
 * agree byte for byte, or a preload hint and the page's own fetch() become
 * two downloads of the same file.
 */
import { CAROUSEL_STRIP_TIERS, CAROUSEL_STRIP_VERSION } from "../../generated/carouselStripVersion";
import { ASSET_TIER } from "../../utils/assetTier";

/** This tier's strip: width, full virtual height, and each page's rows. */
export const STRIP_LAYOUT = CAROUSEL_STRIP_TIERS[ASSET_TIER];

/** One URL per page, in page order. */
export const CAROUSEL_STRIP_PAGE_URLS: readonly string[] = STRIP_LAYOUT.pages.map(
    (_, i) =>
        `/assets/textures/portfolio/carousel-strip${ASSET_TIER === "sm" ? "-sm" : ""}-${i}.avif?v=${CAROUSEL_STRIP_VERSION}`,
);

let stripPrefetched = false;

/** Warms the strip pages for a later visit to the carousel, as low-priority
 *  prefetch hints. For routes where the carousel is NOT mounted (the book,
 *  the 404): where it is, Portfolio.tsx is already fetching the same URLs,
 *  and a prefetch alongside an in-flight fetch() is a second download, not
 *  a cache hit. Idempotent, so StrictMode's double effect adds one set. */
export function prefetchCarouselStripPages(): void {
    if (stripPrefetched) return;
    stripPrefetched = true;
    for (const href of CAROUSEL_STRIP_PAGE_URLS) {
        const link = document.createElement("link");
        link.rel = "prefetch";
        link.href = href;
        link.as = "image";
        document.head.appendChild(link);
    }
}
